import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchUserTrips } from "../services/api";
import { motion } from "framer-motion";

const TripsDashboard = ({ compact = false, highlightTrip = null }) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll ,setShowAll] = useState(false);
  const visibleTrips = showAll ? trips : trips.slice(0, 6);
  const navigate = useNavigate();

useEffect(() => {
  const loadTrips = async () => {
    try {
      const data = await fetchUserTrips();

      if (highlightTrip) {
        const onlyNewTrip = data.filter(
          (trip) => trip.travelKeyToken === highlightTrip
        );
        setTrips(onlyNewTrip);
      } else {
        setTrips(data);
      }
    } catch (err) {
      console.error("Failed to load trips");
    } finally {
      setLoading(false);
    }
  };

  loadTrips();
}, [highlightTrip]);

  if (loading) {
    return (
      <div className="py-24 text-center">
        <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-400 font-medium tracking-wide italic">Preparing your journeys...</p>
      </div>
    );
  }

  if (!trips.length) {
    return (
      <div className="py-24 text-center max-w-md mx-auto">
        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">No trips discovered yet</h3>
        <p className="text-gray-500 mb-8">Ready to start your next adventure? Create your first trip plan to see it here.</p>
        <button 
          onClick={() => navigate("/plan")}
          className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition"
        >
          Plan New Trip
        </button>
      </div>
    );
  }

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="space-y-10">
      {!compact && (
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="text-4xl font-black text-gray-900 tracking-tight">
              Your Collections
            </h2>
            <p className="text-gray-500 font-medium mt-1">Explore and manage your upcoming adventures.</p>
          </div>
          <button 
            onClick={() => navigate("/plan")}
            className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-xl font-bold shadow-sm hover:shadow-md hover:border-indigo-100 hover:text-indigo-600 transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            New Trip
          </button>
        </div>
      )}

      <motion.div 
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
      >
        {visibleTrips.map((trip) => {
          const cityData = trip.cities?.[0];
          const cityName = cityData?.cityName || "Untitled Trip";
          const startDate = cityData?.startDate;
          const daysCount = cityData?.itineraryDays?.length || 1;
          const isExpired = new Date() > new Date(trip.shareExpiresAt);

          return (
            <motion.div
              key={trip._id}
              variants={item}
              whileHover={{ y: -8 }}
              onClick={() => navigate(`/trip/${trip.travelKeyToken}`)}
              className="group relative bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm hover:shadow-2xl transition-all cursor-pointer overflow-hidden"
            >
              {/* Decorative Accent */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/50 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>

              <div className="relative">
                <div className="flex justify-between items-start mb-6">
                  <div className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                    isExpired ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"
                  }`}>
                    {isExpired ? "Archived" : "Active"}
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0">
                    <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white shadow-lg">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <h3 className="text-3xl font-black text-gray-900 leading-tight mb-2 group-hover:text-indigo-600 transition-colors">
                  {cityName}
                </h3>

                <div className="flex items-center gap-2 text-gray-400 font-bold text-xs uppercase tracking-wider mb-8">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {daysCount} {daysCount === 1 ? "Day" : "Days"} Journey
                </div>

                <div className="pt-6 border-t border-gray-50 flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">Departure</span>
                    <span className="text-sm font-bold text-gray-700">
                      {startDate ? new Date(startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : "Not Scheduled"}
                    </span>
                  </div>
                  
                  <div className="flex flex-col items-end text-right">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">Link Expiry</span>
                    <span className="text-sm font-bold text-gray-400">
                      {new Date(trip.shareExpiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
{trips.length > 6 && (
  <div className="flex justify-center items-center mt-10">

    <button
      onClick={() => setShowAll(!showAll)}
      className="group inline-flex items-center gap-3 px-8 py-3.5 rounded-2xl bg-white border border-gray-200 text-gray-900 text-sm font-semibold shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200"
    >
      
      <span>
        {showAll ? "Show Less" : "View All Trips"}
      </span>

      {/* Arrow */}
      <span className="transition-transform duration-200 group-hover:translate-x-1">
        →
      </span>

    </button>

  </div>
)}
      </motion.div>
    </div>
  );
};

export default TripsDashboard;
